import { useCallback, useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { AuthRTKService } from "../../app/services/auth-rtk.service";
import {
  useLazyGetPlanInfoQuery,
  useValidatePromotionCodeMutation
} from "../../app/services/billing-rtk.service";
import { ChatbotRTKService } from "../../app/services/chatbot-rtk.services";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import { selectUseId, setIsUserNew } from "../../features/auth/auth.slice";

const WelcomeTrialModal = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const [getPlanInfo] = useLazyGetPlanInfoQuery();

  const userId = useAppSelector(selectUseId);

  const [vlidatePromotionCode, ValidatePromotionCodeResults] =
    useValidatePromotionCodeMutation();

  const [show, setShow] = useState(false);

  const checkPromoCode = () => {
    const promoCode = "UNLOCK_ALL_WELCOME";

    vlidatePromotionCode({ code: promoCode })
      .unwrap()
      .then((res) => {
        dispatch(
          ChatbotRTKService.util.invalidateTags(["Package", "GetAllScenarios"])
        );

        dispatch(AuthRTKService.util.invalidateTags(["Profile"]));

        toast.success(t("BillingCreatePage/l-04"));

        dispatch(setIsUserNew(false));

        localStorage.setItem(`louise_ignore_welcome_${userId}`, "true");

        setShow(false);

        window.location.href = `/${
          MultiLanguageUrl[i18n.language]
        }/verify-plan?plan_name=${promoCode}&status=true`;
      })
      .catch((res) => {
        const codeMessage =
          res?.data?.code && t(`BillingCreatePage/${res.data.code}`);
        const defaultMessage = t("BillingCreatePage/l-03");

        if (codeMessage) {
          toast.warning(codeMessage);
        } else if (res?.data?.message) {
          toast.warning(res.data.message);
        } else {
          toast.warning(defaultMessage);
        }
      });
  };

  const handleCloseWelcome = () => {
    localStorage.setItem(`louise_ignore_welcome_${userId}`, "true");
    setShow(false);
  };

  const shouldShowUpgradeModal = useCallback(async () => {
    if (userId) {
      let hasIgnoredWelcomeAlready = localStorage.getItem(
        `louise_ignore_welcome_${userId}`
      );
      if (!!hasIgnoredWelcomeAlready || !hasIgnoredWelcomeAlready) {
        try {
          const result = await getPlanInfo();
          const has_only_free_plan = result?.data?.filter(
            (x) => x.name !== "CHATBOT_FREE"
          )?.length;

          if (has_only_free_plan > 0) {
            localStorage.setItem(`louise_ignore_welcome_${userId}`, "true");
            hasIgnoredWelcomeAlready = "true";
          }
        } catch (error) {
          console.error("Error fetching plan info:", error);
        }
      }

      const status = searchParams.get("status");
      if (!hasIgnoredWelcomeAlready && (status == null || status === "true")) {
        setShow(true);
      }
    }
  }, [, userId]);

  useEffect(() => {
    shouldShowUpgradeModal();
  }, [shouldShowUpgradeModal]);

  return (
    <Modal show={show} onHide={handleCloseWelcome} centered>
      <Modal.Body
        style={{
          backgroundImage:
            "url(https://cdn.1cdn.app/application/LOUISEBOT/24111211523329_congrats.svg)",
          backgroundRepeat: "no-repeat",
          backgroundPositionY: "-20px",
          backgroundPositionX: "center"
        }}
      >
        <div className="tw-pt-[100px] pb-10 tw-flex-col tw-justify-center tw-items-center tw-gap-6 tw-inline-flex">
          <div className="tw-self-stretch  tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-flex tw-mb-6">
            <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
              <div className="tw-text-gray-700 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-9">
                {t("WelcomeTrialModal/key1")}
              </div>
              <div className="tw-w-5 tw-h-5 tw-p-0.5 tw-origin-top-left tw-rotate-180 tw-opacity-0 tw-justify-center tw-items-center tw-flex" />
            </div>
            <div className="tw-self-stretch tw-text-center tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
              {t("WelcomeTrialModal/key2")}
            </div>
          </div>
          <div className="tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
            <button
              disabled={ValidatePromotionCodeResults.isLoading}
              className="tw-px-4 tw-py-2.5 tw-border-none tw-bg-primary tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-gap-2 tw-inline-flex tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              onClick={checkPromoCode}
            >
              {ValidatePromotionCodeResults.isLoading ? (
                <Spinner size="sm" />
              ) : (
                t("WelcomeTrialModal/key3")
              )}
            </button>
            <button
              disabled={ValidatePromotionCodeResults.isLoading}
              className="tw-px-4 tw-py-2.5 tw-border-none tw-justify-center tw-items-center tw-gap-2 tw-inline-flex tw-bg-transparent tw-text-primary tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              onClick={handleCloseWelcome}
            >
              {t("WelcomeTrialModal/key4")}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default WelcomeTrialModal;
